import { useMemo, useState } from 'react';
import { Button, List, Typography, Alert, Space, Card, Row, Col, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import {
  MakeDepositsRequest,
  MakeDepositsResponse,
  MakeWithdrawalsRequest,
  MakeWithdrawalsResponse,
  TransactionInfo,
} from '@/util/transfer-util';
import { useAppSelector } from '@/store/hooks';
import { selectTransferState } from '@/store/features/transfer/transfer-selectors';
import { setTransferState } from '@/store/features/transfer/transferSlice';
import { makeDeposits, makeWithdrawals, isAmplifyError } from '@/api/custom';
import { formatCurrency } from '@wavvest/shared-utils';
import { formatAccountType } from '@/util/account-type-format';
import { useCustodianAccounts } from '@/hooks/useCustodianAccounts';
import { useTheme } from '@/contexts/ThemeContext';
import { findDepositSourceAccount } from './deposit-account-selection';

const { Title, Text } = Typography;

export default function ReviewDeposit() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const globalPlaidAccounts = useAppSelector(selectPlaidAccounts);
  const globalTransferState = useAppSelector(selectTransferState);
  const plaidAccounts = useMemo(() => globalPlaidAccounts, [globalPlaidAccounts]);
  const transferState = useMemo(() => globalTransferState, [globalTransferState]);

  // Legacy per-account Apex accounts for a legacy Apex firm, or firm-data's
  // blended custodian accounts (real Apex ids via custodian_account_id) for
  // a firm-data + Apex firm — used only for the "To" display label below.
  const { accounts: apexAccounts } = useCustodianAccounts();
  const [submissionState, setSubmissionState] = useState<
    | {
        state: 'waitingUserConfirmation';
      }
    | {
        state: 'waitingServerResponse';
      }
    | {
        state: 'error';
      }
    | {
        state: 'finished';
        response: MakeDepositsResponse | MakeWithdrawalsResponse;
      }
  >({
    state: 'waitingUserConfirmation',
  });
  if (transferState.state !== 'Transferring') {
    return null;
  }

  const submitTransfers = async () => {
    if (transferState.state !== 'Transferring') {
      return;
    }
    setSubmissionState({ state: 'waitingServerResponse' });
    const transactions = transferState.transactions;

    try {
      let response: MakeDepositsResponse | MakeWithdrawalsResponse;
      if (transactions.some((v) => v.kind == 'deposit')) {
        const request: MakeDepositsRequest[] = transactions.map((v) => ({
          amount: v.amount,
          account_id: v.custodianAccountId!,
          plaid_id: v.plaidAccountId!,
          ira_contribution: v.iraContributionType
            ? {
                tax_year: v.iraContributionYear!,
                type: v.iraContributionType,
              }
            : undefined,
        }));
        response = await makeDeposits(request);
      } else {
        const request: MakeWithdrawalsRequest[] = transactions.map((v) => ({
          amount: v.amount,
          account_id: v.custodianAccountId!,
          plaid_id: v.plaidAccountId!,
          ira_distribution: v.iraDistributionType
            ? {
                type: v.iraDistributionType,
                federal_tax_withholding: v.iraDistributionFederalWithhold
                  ? {
                      amount: {
                        value: v.iraDistributionFederalWithhold.toString(),
                      },
                    }
                  : undefined,
                state_tax_withholding: v.iraDistributionStateWithhold
                  ? {
                      amount: {
                        value: v.iraDistributionStateWithhold.toString(),
                      },
                    }
                  : undefined,
                state_withholding_waiver: v.iraDistributionStateWithholdWaiver,
              }
            : undefined,
        }));
        response = await makeWithdrawals(request);
      }
      if (response.failed.length > 0) {
        let message = 'Some deposits has failed to be made.\n';
        message +=
          'If you have recently connected a new external account, the bank might have not yet fully processed that information, which will be resolved in a few minutes.\n';
        message += 'Please try again later.';
        alert(message);
        console.warn(message);
        console.warn(response.failed);
      }
      setSubmissionState({ state: 'finished', response });
    } catch (e) {
      if (isAmplifyError(e)) {
        alert('An error occurred. Please try again later. \n' + e.response.data);
        console.error('An error occurred while making transfers: ', e.response.data);
      } else {
        alert('An error occurred. Please try again later. \n' + e);
        console.error('An error occurred while making transfers: ', e);
      }
      setSubmissionState({ state: 'error' });
      return;
    }
  };

  if (transferState.state !== 'Transferring') {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="No Transfer Data"
          description="No transfer data available"
          type="warning"
          showIcon
        />
      </div>
    );
  }

  // 添加主题样式类
  const containerClass = `review-transfer-container ${theme}-theme`;

  return (
    <div className={containerClass} style={{ padding: 24, minHeight: '100vh' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={2}>Review Transfer</Title>

        <Card>
          <List
            itemLayout="vertical"
            dataSource={transferState.transactions}
            renderItem={(item: TransactionInfo) => {
              const account = apexAccounts.find((v) => v.id === item.custodianAccountId);
              const plaidAccount = findDepositSourceAccount(plaidAccounts, item.plaidAccountId);
              const bankLabel = plaidAccount?.name || 'Linked bank account';
              const apexLabel = `${account ? formatAccountType(account.type) : 'Unknown Account'}(${
                account?.name || 'Unknown'
              })`;
              const isWithdraw = item.kind === 'withdraw';

              return (
                <List.Item>
                  <Card size="small">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Space direction="vertical" size="small">
                          <Text strong>{item.kind === 'deposit' ? 'Deposit' : 'Withdrawal'}</Text>
                          <Text type="secondary">
                            Amount:{' '}
                            <Text strong style={{ color: '#1890ff' }}>
                              {formatCurrency(item.amount)}
                            </Text>
                          </Text>
                        </Space>
                      </Col>
                      <Col span={12}>
                        <Space direction="vertical" size="small">
                          <div>
                            <Text type="secondary">From: </Text>
                            <Text>{isWithdraw ? apexLabel : bankLabel}</Text>
                          </div>
                          <div>
                            <Text type="secondary">To: </Text>
                            <Text>{isWithdraw ? bankLabel : apexLabel}</Text>
                          </div>
                        </Space>
                      </Col>
                    </Row>

                    {/* IRA specific fields */}
                    {(item.iraContributionType || item.iraDistributionType) && (
                      <div style={{ marginTop: 16, paddingTop: 16 }}>
                        <Space direction="vertical" size="small">
                          {item.iraContributionType && (
                            <Text type="secondary">
                              Contribution Type: <Text>{item.iraContributionType}</Text>
                            </Text>
                          )}
                          {item.iraContributionYear && (
                            <Text type="secondary">
                              Contribution Year: <Text>{item.iraContributionYear}</Text>
                            </Text>
                          )}
                          {item.iraDistributionType && (
                            <Text type="secondary">
                              Distribution Type: <Text>{item.iraDistributionType}</Text>
                            </Text>
                          )}
                        </Space>
                      </div>
                    )}
                  </Card>
                </List.Item>
              );
            }}
          />
        </Card>

        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Title level={4}>Confirm Transfer</Title>
            <Text type="secondary">
              Please review the transfer details above. Once confirmed, the transfer will be
              processed.
            </Text>

            <Row gutter={16}>
              <Col>
                <Button
                  size="large"
                  onClick={() => navigate(-1)}
                  disabled={submissionState.state == 'waitingServerResponse'}
                >
                  Back
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  size="large"
                  loading={submissionState.state == 'waitingServerResponse'}
                  disabled={submissionState.state == 'waitingServerResponse'}
                  onClick={() => {
                    if (submissionState.state == 'waitingUserConfirmation') {
                      submitTransfers();
                    } else {
                      message.success('Transfer completed');
                      setTransferState({
                        state: 'None',
                      });
                      navigate('/home/wealth');
                    }
                  }}
                >
                  {submissionState.state == 'waitingUserConfirmation' ? 'Done' : 'Confirm Transfer'}
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>
      </Space>
    </div>
  );
}
